'use client';

import React, {
	createContext,
	ReactNode,
	useCallback,
	useContext,
	useEffect,
	useState,
} from 'react';
import {
	DEFAULT_LOCALE,
	getPreferredLocale,
	Locale,
	setLocale as setAppLocale,
} from '@/i18n';

type I18nContextType = {
	locale: Locale;
	setLocale: (lang: Locale) => Promise<void>;
};

export const I18nContext = createContext<I18nContextType>({
	locale: DEFAULT_LOCALE,
	setLocale: async () => {},
});

type I18nProviderProps = {
	children: ReactNode;
};

export const I18nProvider = ({ children }: I18nProviderProps) => {
	const [locale, setLocaleState] = useState<Locale>(DEFAULT_LOCALE);
	const setLocale = useCallback(async (nextLocale: Locale) => {
		await setAppLocale(nextLocale);
		setLocaleState(nextLocale);
	}, []);

	// Refresh preferred locale on mount, as this happens only on the client
	useEffect(() => {
		void setLocale(getPreferredLocale());
	}, [setLocale]);

	return (
		<I18nContext.Provider value={{ locale, setLocale }}>
			{children}
		</I18nContext.Provider>
	);
};

export const useLanguage = (): I18nContextType => {
	const context = useContext(I18nContext);
	if (!context) {
		throw new Error('useLanguage must be used within a LanguageProvider');
	}
	return context;
};
